/**
 * Composable for rendering markdown content with support for internal cross-references
 */

import { ref, computed } from 'vue';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { useRouter } from 'vue-router';

/**
 * Rewrite container-directive fences (`:::name ... :::`) into blank-line-padded
 * <div class="name"> blocks so marked parses the inner content as markdown.
 * The name is restricted to a safe css-identifier charset; unmatched fences are
 * left untouched.
 */
function expandDirectives(content: string): string {
  return content.replace(
    /^:::([a-z][a-z0-9-]*)[ \t]*\r?\n([\s\S]*?)\r?\n:::[ \t]*$/gm,
    (_match, name: string, body: string) =>
      `<div class="${name}">\n\n${body.trim()}\n\n</div>`
  );
}

export function useMarkdown() {
  const router = useRouter();

  /**
   * Convert markdown to HTML with sanitization
   */
  const renderMarkdown = (markdownContent: string): string => {
    if (!markdownContent) return '';

    // Expand container directives (callouts) before parsing.
    // Authors write a clean fence in the source markdown:
    //
    //   :::key-insights
    //   - **Point one.**
    //   - **Point two.**
    //   :::
    //
    // marked doesn't understand directive fences, so we rewrite them into a
    // blank-line-padded <div class="name"> block. The blank lines let marked
    // parse the inner text as real markdown, and DOMPurify already allowlists
    // div + class, so the box survives sanitization. Generic by design: the
    // class name is the directive name, so :::note / :::warning style hooks can
    // be added in CSS later without touching this code. Styled today:
    // .key-insights (see styles_coreapp/components.css).
    markdownContent = expandDirectives(markdownContent);

    // Configure marked with custom renderer to add IDs to headers
    const renderer = new marked.Renderer();
    const originalHeading = renderer.heading.bind(renderer);

    renderer.heading = function(text: string, level: number) {
      // Generate slug from heading text
      const slug = text
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();

      return `<h${level} id="${slug}">${text}</h${level}>`;
    };

    marked.setOptions({
      breaks: true, // Convert \n to <br>
      gfm: true,    // GitHub Flavored Markdown
      renderer: renderer,
    });

    // Convert markdown to HTML
    const rawHtml = marked.parse(markdownContent) as string;

    // Sanitize HTML to prevent XSS
    const cleanHtml = DOMPurify.sanitize(rawHtml, {
      ALLOWED_TAGS: [
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'p', 'br', 'hr',
        'strong', 'em', 'u', 'del', 'code', 'pre',
        'ul', 'ol', 'li',
        'a', 'img',
        'blockquote',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'div', 'span'
      ],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'id']
    });

    return cleanHtml;
  };

  /**
   * Process internal cross-reference links (framework://, policy://, process://)
   * These links should navigate within the app rather than external pages
   */
  const handleInternalLink = (event: MouseEvent) => {
    const target = event.target as HTMLElement;

    // Check if clicked element is a link
    if (target.tagName === 'A') {
      const href = target.getAttribute('href');

      if (href && isInternalLink(href)) {
        event.preventDefault();
        navigateToInternalLink(href);
      }
    }
  };

  /**
   * Check if a link is an internal cross-reference
   */
  const isInternalLink = (href: string): boolean => {
    return href.startsWith('framework://') ||
           href.startsWith('policy://') ||
           href.startsWith('process://');
  };

  /**
   * Navigate to an internal cross-reference link
   */
  const navigateToInternalLink = (href: string) => {
    // Parse the internal link
    const [domain, itemId] = href.split('://');

    console.log(`Navigating to ${domain} domain, item ${itemId}`);

    // TODO: Implement navigation logic
    // This would emit an event or use the router to navigate
    // to the appropriate section in the explorer

    // For now, just log the navigation intent
    // In full implementation, this would:
    // 1. Switch to the correct domain (framework/policy/process)
    // 2. Select the item with the given ID
    // 3. Scroll to that item in the navigation tree
  };

  /**
   * Extract heading structure for table of contents
   */
  const extractHeadings = (markdownContent: string) => {
    const headings: Array<{ level: number; text: string; id: string }> = [];
    const lines = markdownContent.split('\n');

    lines.forEach((line) => {
      const match = line.match(/^(#{1,6})\s+(.+)$/);
      if (match) {
        const level = match[1].length;
        const text = match[2].trim();
        const id = text.toLowerCase().replace(/[^\w]+/g, '-');

        headings.push({ level, text, id });
      }
    });

    return headings;
  };

  /**
   * Parse YAML frontmatter from markdown content
   */
  const parseFrontmatter = (content: string) => {
    const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n/;
    const match = content.match(frontmatterRegex);

    if (!match) {
      return { frontmatter: null, content };
    }

    // Extract frontmatter and remaining content
    const frontmatterText = match[1];
    const remainingContent = content.slice(match[0].length);

    // Simple YAML parsing (for basic key-value pairs)
    const frontmatter: Record<string, any> = {};
    frontmatterText.split('\n').forEach((line) => {
      const colonIndex = line.indexOf(':');
      if (colonIndex > 0) {
        const key = line.slice(0, colonIndex).trim();
        const value = line.slice(colonIndex + 1).trim().replace(/^["']|["']$/g, '');
        frontmatter[key] = value;
      }
    });

    return { frontmatter, content: remainingContent };
  };

  return {
    renderMarkdown,
    handleInternalLink,
    isInternalLink,
    navigateToInternalLink,
    extractHeadings,
    parseFrontmatter
  };
}
